import * as React from 'react';
import {Helmet} from 'react-helmet';
import {generatePath} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, RouterHistory} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';

import type {FilterObject} from 'nutshell-core/types';

import {headTitle} from 'shells/document/head-title';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';

import type {
    BroadcastFragment as Broadcast,
    BroadcastStubFragment as BroadcastStub,
    EditionTestEmailSend,
    EditionTestEmailSendVariables,
    EditionUpdateInput,
} from 'nutshell-graphql-types';

import type {ReportURLParams} from '../../reports/report-types';

import {EditionPage} from '../editions/edition-page';
import EDITION_TEST_EMAIL_SEND from '../editions/graphql/mutations/edition-test-email-send.graphql';
import {SavingDebounce} from '../editions/utils/saving-debounce';
import {BroadcastMutations} from '../graphql-wrappers/broadcast/broadcast-mutations';
import {BroadcastQuery} from '../graphql-wrappers/broadcast/broadcast-query';

type OwnProps = ContextRouter;

type Props = OwnProps & {
    history: RouterHistory;
    broadcast: Broadcast | null | undefined;
    broadcastStub: BroadcastStub | null | undefined;
    isLoading: boolean;
    onSendEmail: (toEmailAddress: string[]) => Promise<any>;
    onBroadcastSaveDebounced: (editionId: string, beeBodyJson: string) => Promise<any>;
    onBroadcastSave: (saveData: EditionUpdateInput) => Promise<any>;
    isSaving: boolean;
    savingError?: string;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onRemoveAllFilters: () => void;
    error: string | null | undefined;
};

export function BroadcastPageWrapperComponent(props: Props) {
    const setSelectedEditorTab = (newTab: string) => {
        const newPath = generatePath(props.match.path, {
            broadcastId: props.match.params.broadcastId,
            editionId: props.match.params.editionId,
            tab: newTab,
        });

        // Make sure to pass the search here, because without it we force a
        // rerender, which causes BEE to reload.
        props.history.replace({
            pathname: newPath,
            search: props.location.search,
        });
    };

    if (props.error) {
        return <ErrorState errorMessage={props.error} />;
    }

    if (props.isLoading) {
        return <LoadingPage />;
    }

    const broadcast = props.broadcast;
    const pageTitle = broadcast && `${broadcast.name}`;

    return (
        <>
            <Helmet>
                <title>{headTitle(pageTitle)}</title>
            </Helmet>
            <EditionPage
                {...props}
                pageTitle={headTitle(pageTitle)}
                onChange={props.onBroadcastSaveDebounced}
                onEditionSave={props.onBroadcastSave}
                edition={props.broadcast ? props.broadcast.edition : null}
                editionStub={props.broadcastStub ? props.broadcastStub.edition : null}
                onSetSelectedEditorTab={setSelectedEditorTab}
                campaignType='BROADCAST'
                generatePath={(newTab: string) => {
                    const path = generatePath(props.match.path, {
                        broadcastId: props.match.params.broadcastId,
                        editionId: props.match.params.editionId,
                        tab: newTab,
                    });

                    return props.location.search && props.location.search.length
                        ? `${path}${props.location.search}`
                        : path;
                }}
            />
        </>
    );
}

export function BroadcastPageWrapper(props: OwnProps) {
    const broadcastIdParam = props.match.params.broadcastId;
    const broadcastApiId = broadcastIdParam ? broadcastIdParam : '';

    const [sendTestEmail] = useMutation<EditionTestEmailSend, EditionTestEmailSendVariables>(
        EDITION_TEST_EMAIL_SEND
    );

    return (
        <BroadcastQuery broadcastId={broadcastApiId}>
            {({broadcast, broadcastStub, isLoading, error}) => (
                <BroadcastMutations>
                    {({onUpdate}) => (
                        <SavingDebounce
                            onSave={(saveData: EditionUpdateInput) => {
                                return onUpdate(saveData, broadcastApiId);
                            }}
                        >
                            {({onChange, isSaving, hasChanged}) => (
                                <BroadcastPageWrapperComponent
                                    {...props}
                                    broadcast={broadcast}
                                    broadcastStub={broadcastStub}
                                    isLoading={isLoading}
                                    // The behavior in this case is the same if the content has
                                    // changed or if we're saving
                                    isSaving={isSaving || hasChanged}
                                    // @ts-expect-error
                                    onSendEmail={(emailAddresses) => {
                                        const editionId = broadcast && broadcast.edition.id;
                                        if (editionId) {
                                            return sendTestEmail({
                                                variables: {
                                                    input: {
                                                        editionId: editionId,
                                                        emailAddresses: emailAddresses,
                                                    },
                                                },
                                            });
                                        }
                                    }}
                                    onBroadcastSaveDebounced={(
                                        editionId: string,
                                        beeBodyJson: string
                                    ) => {
                                        onChange({editionId, beeBodyJson});
                                    }}
                                    onBroadcastSave={(saveData: EditionUpdateInput) =>
                                        onUpdate(saveData, broadcastApiId)
                                    }
                                    error={error}
                                />
                            )}
                        </SavingDebounce>
                    )}
                </BroadcastMutations>
            )}
        </BroadcastQuery>
    );
}
